import moment from 'moment-timezone';
import DialogService from './dialogs/DialogService';
import UserProfileDialog from '@/components/UserProfile.vue';
import { getCurrentRoute } from '@/utils/routerAccess';
import { exponentialRetry } from '@/utils/utils';
import store from '@/store';
import i18n from '@/i18n';
import { saveValueToLocalstore } from '@/services/localStorage.service';
import loggerService from '@/services/logger.service';
import { getUsersByMmidAndAddtoStore } from '@/store/schedulers';
import { makeUser } from '@/utils/userTransform';
import analytics from '@/analytics';
import { getLastMMSocketState } from '@/services/socketClient/socketStateTracker';
import { saveData } from '@/utils/eventActionUtils';
import {
  createPrivilegedAgoraToken,
  createUnprivilegedAgoraTokenV2,
  createListRegistrantsV2,
  retrieveUber,
  listBroadcasts,
  listDiscussionGroups,
  getP2pDiscussionGroup,
  retrieveDiscussionGroups,
  simpleEventMembers,
  listBooths,
  listTracks,
  logCtaClickBoothEventMembers,
  retrieveEventMembers,
  fmmidEventMembers,
  uuidCachedEventMembers,
  createPostsDelete,
  partialUpdateEventMembers,
  partialUpdateEvent,
  createAsyncAttend,
  partialUpdateBroadcasts,
  recordStartDiscussionGroups,
  recordUpdateLayoutBroadcasts,
  recordUpdateLayoutDiscussionGroups,
  requestCallEventMembers,
  partialUpdateAgendaItem,
  createTechCheck,
  videoModeBroadcasts,
  liveModeBroadcasts,
  createAgendaFavorites,
  destroyAgendaFavorites,
  agoraTokenBroadcasts,
  agoraTokenDiscussionGroups,
  bulkCreateUnprivilegedAgoraTokens,
  getCventEventSurvey,
  getCventEventTheme
} from '@goldcast/api/backend';
import { SEARCH_COLUMN_FULLNAME, FILTER_OPERATOR_CONSTANTS, DEFAULT_ORDER_BY, ORDER_BY_RANDOM } from '@/constants';
import { EVENT_APP_ORG_ID_KEY } from '@/constants/authKeys';

let uberData = null;

export function timeToTz(m = moment()) {
  return moment(m).tz(moment.tz.guess());
}

export function formatDuration(start, end, showKeys = true) {
  // get total seconds between the times
  start = moment(start);
  end = moment(end);

  let seconds = 0;
  if (start.isValid() && end.isValid() && end.isSameOrAfter(start)) {
    seconds = end.diff(start, 'seconds');
  }

  // calculate (and subtract) whole days
  const days = Math.floor(seconds / 86400);
  seconds -= days * 86400;

  // calculate (and subtract) whole hours
  const hours = Math.floor(seconds / 3600) % 24;
  seconds -= hours * 3600;

  // calculate (and subtract) whole minutes
  const minutes = Math.floor(seconds / 60) % 60;
  seconds -= minutes * 60;

  return format(days, hours, minutes, seconds);

  function pad(v) {
    return `${v}`.padStart(2, 0);
  }

  function format(d, h, m, s) {
    const data = { d, h, m, s };
    return Object.keys(data)
      .reduce(
        (acc, key) => (data[key] > 0 || acc.length > 0 ? [...acc, `${pad(data[key])}${showKeys ? key : ''}`] : acc),
        []
      )
      .join(':');
  }
}

/**
 * Formats a given duration in seconds into a human-readable string (e.g., "01:23:45").
 * The output includes days, hours, minutes, and seconds, automatically adjusting based on duration length.
 *
 * @param {number} seconds - The duration in seconds to format.
 * @returns {string} - The formatted duration as a string (e.g., "01:23:45" or "00:45").
 *
 * @example
 * formatDurationFromSeconds(3661); // "01:01:01"
 * formatDurationFromSeconds(59);   // "00:59"
 * formatDurationFromSeconds(86461); // "1:00:01:01"
 */
export function formatDurationFromSeconds(seconds) {
  // calculate (and subtract) whole days
  const days = Math.floor(seconds / 86400);
  seconds -= days * 86400;

  // calculate (and subtract) whole hours
  const hours = Math.floor(seconds / 3600) % 24;
  seconds -= hours * 3600;

  // calculate (and subtract) whole minutes
  const minutes = Math.floor(seconds / 60) % 60;
  seconds -= minutes * 60;

  const duration = { days, hours, minutes, seconds };

  function padTimeWithZeroPrefix(v) {
    return `${v}`.padStart(2, 0);
  }

  let formattedDuration = Object.keys(duration)
    .reduce(
      (acc, key) =>
        duration[key] > 0 || acc.length > 0 ? [...acc, `${padTimeWithZeroPrefix(duration[key])}${''}`] : acc,
      []
    )
    .join(':');

  if (formattedDuration === '') {
    formattedDuration = '00';
  }

  if ((formattedDuration.match(/:/g) || []).length === 0) {
    formattedDuration = `00:${formattedDuration}`;
  }

  return formattedDuration;
}

export async function showUserModal(user, showActions = true, placement = 'top-right', editProfile = false) {
  analytics.track('user_profile_show', {
    profile: user,
    isSelf: store.state.user.currentUser?.id === user.id
  });

  const { default: UserModalDialog } = await import('@/components/UserModalDialog.vue');
  return DialogService.show(
    UserModalDialog,
    { userData: user, showActions, editProfile, id: 'userProfilePopUp' },
    { placement }
  );
}

export function showUserModalUsingUuid(uuid) {
  const eventId = store.state.event.event?.id;

  if (eventId) {
    return getEventUsersById(eventId, [uuid])
      .then(res => {
        if (!res?.data?.length) {
          loggerService.warn('Could not get user from api');
          return Promise.reject('No user found!');
        }
        return makeUser(res.data[0]);
      })
      .then(user => {
        return showUserModal(user);
      })
      .catch(err => {
        loggerService.error('Failed to open user modal: ', err);
      });
  }

  loggerService('EventId not found');
  return null;
}

export function showUserModalUsingmmid(mmid) {
  const eventId = store.state.event.event?.id;
  if (eventId) {
    return getUsersByMmidAndAddtoStore(eventId, [mmid])
      .then(resp => {
        const user = resp[0];
        if (!user) {
          loggerService.warn('Could not get user from api');
          return Promise.reject('No user found!');
        }
        return user;
      })
      .then(user => {
        return showUserModal(user);
      })
      .catch(err => {
        loggerService.error('Failed to open user modal: ', err);
      });
  }
  loggerService('EventId not found');
  return null;
}

export function showEditUserProfileDialog(placement = 'top-right edge') {
  analytics.track('user_profile_edit_dialog_show');
  return DialogService.show(UserProfileDialog, {}, { placement });
}

const UBER_RETRYABLE_CODES = new Set([0, 503, 504]);
const UBER_MAX_RETRIES = 3;
const UBER_BASE_DELAY_MS = 1000;
const UBER_MMID_POLL_INTERVAL_MS = 10000;

let uberMmidPollIntervalId = null;

export function startUberMmidPolling(context, eventId) {
  if (uberMmidPollIntervalId) {
    return;
  }

  loggerService.error(`[Recorder] Uber response missing mmid for event ${eventId}, starting 10s polling`);

  uberMmidPollIntervalId = setInterval(async () => {
    try {
      const res = await retrieveUber({ queryParams: { event_id: eventId } });
      uberData = res;
      if (res?.event?.[0]) {
        const eventUsersLoaded = Promise.resolve([context.rootState.user.currentEventUser]);
        await saveData(context, eventUsersLoaded, uberData);
      }
      if (res?.event?.[0]?.mmid) {
        clearInterval(uberMmidPollIntervalId);
        uberMmidPollIntervalId = null;
        loggerService.warn(`[Recorder] Uber mmid obtained for event ${eventId}, polling stopped`);
      } else {
        loggerService.warn(`[Recorder] Uber response still missing mmid for event ${eventId}, retrying in 10s`);
      }
    } catch (err) {
      loggerService.error(`[Recorder] Error polling Uber for mmid for event ${eventId}:`, err);
    }
  }, UBER_MMID_POLL_INTERVAL_MS);
}

export function stopUberMmidPolling() {
  if (uberMmidPollIntervalId) {
    clearInterval(uberMmidPollIntervalId);
    uberMmidPollIntervalId = null;
  }
}

const UBER_RECORDER_POLL_INTERVAL_MS = 10000;
const UBER_RECORDER_SOCKET_STALE_MS = 10000;

let uberRecorderPollIntervalId = null;

export function startUberPollingForRecorder(eventId) {
  if (uberRecorderPollIntervalId) {
    return;
  }

  loggerService.log(`[Recorder] Starting uber polling for event ${eventId}`);

  uberRecorderPollIntervalId = setInterval(async () => {
    try {
      const { timestamp, specificTeamId } = getLastMMSocketState();
      const eventMmid = store.state.event.event?.mmid;
      const socketIsStale = Date.now() - timestamp > UBER_RECORDER_SOCKET_STALE_MS;
      const socketBelongsToEvent = !!specificTeamId && specificTeamId === eventMmid;

      if (!socketBelongsToEvent || !socketIsStale) {
        loggerService.log(
          `[Recorder] Skipping uber poll: socket is fresh for this event (age=${Date.now() - timestamp}ms)`
        );
        return;
      }

      loggerService.log(
        `[Recorder] Socket stale (${Date.now() - timestamp}ms ago) for event ${eventId}, fetching uber`
      );
      const res = await retrieveUber({ queryParams: { event_id: eventId } });
      uberData = res;
      await store.dispatch('event/applyUberData', res);
    } catch (err) {
      loggerService.error(`[Recorder] Error in uber poll for event ${eventId}:`, err);
    }
  }, UBER_RECORDER_POLL_INTERVAL_MS);
}

export function stopUberPollingForRecorder() {
  if (uberRecorderPollIntervalId) {
    clearInterval(uberRecorderPollIntervalId);
    uberRecorderPollIntervalId = null;
    loggerService.log('[Recorder] Uber polling stopped');
  }
}

function retrieveUberWithBackoff(eventId, extraQueryParams = {}, retryOn429 = false) {
  const retryableCodes = retryOn429 ? new Set([429, ...UBER_RETRYABLE_CODES]) : UBER_RETRYABLE_CODES;
  return exponentialRetry(() => retrieveUber({ queryParams: { event_id: eventId, ...extraQueryParams } }), {
    retries: UBER_MAX_RETRIES + 1,
    delayMs: UBER_BASE_DELAY_MS,
    shouldRetry: err => retryableCodes.has(err?.statusCode),
    jitterMs: 500,
    onRetry: (err, attempt) => loggerService.warn(`Uber API ${err.statusCode} — attempt ${attempt} failed`)
  });
}

function applyCventThemeToUberEvent(event, themeData = {}) {
  if (!event || !themeData.theme_obj) {
    return event;
  }

  const theme_obj = themeData.theme_obj;

  return {
    ...event,
    theme_obj,
    ondemand_builder_config: { ...event.ondemand_builder_config, ...themeData.ondemand_builder_config },
    logo_url: themeData.logo_url
  };
}

export function getEventFromUberEndpoint(eventId, forceUpdate = false, extraQueryParams = {}, retryOn429 = false) {
  return new Promise((resolve, reject) => {
    if (!forceUpdate && uberData) {
      resolve({ data: uberData });
      return;
    }
    retrieveUberWithBackoff(eventId, extraQueryParams, retryOn429)
      .then(async res => {
        if (res?.event?.[0]?.event_creation_source === 'cvent_webinar') {
          const cventTheme = await getCventEventTheme({ event_id: eventId }).catch(() => null);
          if (cventTheme) {
            const event = res?.event?.pop();
            res.event = [applyCventThemeToUberEvent(event, cventTheme)];
          }
        }
        uberData = res;
        if (uberData?.organization?.id) {
          saveValueToLocalstore(EVENT_APP_ORG_ID_KEY, uberData.organization.id);
        }
        resolve({ data: res, statusCode: 200, statusText: 'OK' });
      })
      .catch(err => {
        reject(err);
      });
  });
}

export function getCachedUberData() {
  return uberData;
}

export async function getBroadcastDetails(eventId) {
  const response = await listBroadcasts({ queryParams: { event: eventId } });
  return { data: response, statusCode: 200, statusText: 'OK' };
}

export async function loadDiscussionGroups(params) {
  const response = await listDiscussionGroups({ queryParams: params });
  return { data: response, statusCode: 200, statusText: 'OK' };
}

export async function loadDiscussionGroupP2P(params) {
  const response = await getP2pDiscussionGroup({ queryParams: params });
  return { data: response, statusCode: 200, statusText: 'OK' };
}

/**
 * @param {string} [id] - id of discussiongroup
 */
export async function loadDiscussionGroupByID(id) {
  const response = await retrieveDiscussionGroups({ id });
  return { data: response, statusCode: 200, statusText: 'OK' };
}

function setUsernamesToNotNull(res) {
  if (res && res.data && res.data.results && res.data.results.length) {
    res.data.results = res.data.results.map(user => {
      if (!user.name) {
        user.name = '';
      }
      return user;
    });
  } else if (res && res.data && res.data.length) {
    res.data = res.data.map(user => {
      if (!user.name) {
        user.name = '';
      }
      return user;
    });
  }
}

export async function getEventUsers(params) {
  const useRegistrantListApi = store.state.event.useRegistrantListApi;
  if (useRegistrantListApi) {
    const filters = [
      {
        name: 'entered',
        op: FILTER_OPERATOR_CONSTANTS.EQUALS,
        values: [true]
      }
    ];

    if (params.search) {
      filters.push({
        name: SEARCH_COLUMN_FULLNAME,
        op: FILTER_OPERATOR_CONSTANTS.ICONTAINS,
        values: [params.search]
      });
    }

    const payload = {
      filters,
      order_by:
        params?.ordering === 'user__first_name'
          ? SEARCH_COLUMN_FULLNAME
          : Boolean(params?.ordering)
            ? DEFAULT_ORDER_BY
            : ORDER_BY_RANDOM
    };

    const { event, offset, limit } = params;

    const res = await createListRegistrantsV2({
      id: event,
      body: payload,
      queryParams: { offset, limit }
    });

    setUsernamesToNotNull(res);
    return res;
  } else {
    const response = await simpleEventMembers({ queryParams: params });
    const res = { data: response, statusCode: 200, statusText: 'OK' };
    setUsernamesToNotNull(res);
    return res;
  }
}

export async function LoadEventBooths(params) {
  delete params.limit;
  const response = await listBooths({ queryParams: params });
  return { data: response, statusCode: 200, statusText: 'OK' };
}

export async function getTracks(eventID) {
  const response = await listTracks({ queryParams: { event: eventID, custom: false } });
  const data = response.map(track => {
    if (
      !track.agenda_items ||
      (Array.isArray(track.agenda_items) && track.agenda_items.length === 0) ||
      track.agenda_items === ''
    ) {
      loggerService.error('Track has empty agenda_items at parse time', { trackId: track.id, trackTitle: track.title });
    }
    return {
      ...track,
      label: track.title,
      value: track.id,
      agenda_items: (track.agenda_items || '').split(',')
    };
  });
  return { data, statusCode: 200, statusText: 'OK' };
}

export async function logBoothCtaClick(payload) {
  const response = await logCtaClickBoothEventMembers({ body: payload });
  return { data: response, statusCode: 200, statusText: 'OK' };
}

export async function getEventUserData(eventUserId) {
  const response = await retrieveEventMembers({ id: eventUserId });
  return { data: response, statusCode: 200, statusText: 'OK' };
}
export async function getEventUsersBymmid(event, users) {
  const payload = { event };
  if (users && users.length > 0) {
    payload.users = users;
  } else if (users && users.length === 0) {
    return Promise.resolve({ data: [], statusCode: 200, statusText: 'OK' });
  }

  const response = await fmmidEventMembers({ queryParams: { includeBlocked: 'True' }, body: payload });
  return { data: response, statusCode: 200, statusText: 'OK' };
}

/**
 *
 * @param {string} event
 * @param {string[]} users An array of user_ids
 */
export async function getEventUsersById(event, users) {
  const validUsers = (users || []).filter(Boolean);
  if (validUsers.length === 0) {
    return Promise.reject();
  }
  const response = await uuidCachedEventMembers({ body: { event, users: validUsers } });
  return { data: response, statusCode: 200, statusText: 'OK' };
}

export async function deletePost(event_id, post_ids) {
  const response = await createPostsDelete({ id: event_id, body: { post_ids } });
  return { data: response, statusCode: 200, statusText: 'OK' };
}

export function updateEventUser(event, id, params, eventUserId) {
  let eventUserIdP;
  if (eventUserId) {
    eventUserIdP = Promise.resolve(eventUserId);
  } else {
    eventUserIdP = getEventUsersById(event, [id])
      .then(res => {
        return (res && res.data && res.data[0] && res.data[0].id) || null;
      })
      .catch(err => {
        loggerService.error('Failed to fetch eventUser!', err);
        return null;
      });
  }

  return eventUserIdP.then(async eventuserid => {
    if (eventuserid) {
      const response = await partialUpdateEventMembers({ id: eventuserid, body: params });
      return { data: response, statusCode: 200, statusText: 'OK' };
    }
    return Promise.reject(i18n.global.t('@@translate@@ No event user found so attended not marked!'));
  });
}

export async function updateEvent(id, params) {
  const response = await partialUpdateEvent({ id, body: params });
  return { data: response, statusCode: 200, statusText: 'OK' };
}

export async function attendEventAsync(event) {
  const response = await createAsyncAttend({ id: event });
  return { data: response, statusCode: 200, statusText: 'OK' };
}

export async function updateBroadcast(id, params) {
  const response = await partialUpdateBroadcasts({ id, body: params });
  return { data: response, statusCode: 200, statusText: 'OK' };
}

export async function startDiscussionRoomRecording(id) {
  const response = await recordStartDiscussionGroups({ id });
  return { data: response, statusCode: 200, statusText: 'OK' };
}

export async function updateBroadcastRecordingLayout(id, screen_uid) {
  const response = await recordUpdateLayoutBroadcasts({ id, body: { screen_uid } });
  return { data: response, statusCode: 200, statusText: 'OK' };
}

export async function updateDiscussionRoomRecordingLayout(id, screen_uid) {
  const response = await recordUpdateLayoutDiscussionGroups({ id, body: { screen_uid } });
  return { data: response, statusCode: 200, statusText: 'OK' };
}

export async function createP2PVideoCall(eventMemberId) {
  const response = await requestCallEventMembers({ id: eventMemberId });
  return { data: response, statusCode: 200, statusText: 'OK' };
}

export async function toggleBroadcastLiveness(broadcastID, is_live, live_start_time) {
  const response = await partialUpdateBroadcasts({ id: broadcastID, body: { is_live, live_start_time } });
  return { data: response, statusCode: 200, statusText: 'OK' };
}

export async function toggleAgendaItemLiveness(id, is_live, live_start_time) {
  const response = await partialUpdateAgendaItem({ id, body: { is_live, live_start_time } });
  return { data: response, statusCode: 200, statusText: 'OK' };
}

export async function reportTechCheck(data) {
  const response = await createTechCheck({ body: data });
  return { data: response, statusCode: 200, statusText: 'OK' };
}

export async function playVideoInLiveBroadcast(broadcastId, videoId) {
  const response = await videoModeBroadcasts({ id: broadcastId, body: { video_asset: videoId } });
  return { data: response, statusCode: 200, statusText: 'OK' };
}

export async function stopPlayingVideoInLiveBroadcast(broadcastId) {
  const response = await liveModeBroadcasts({ id: broadcastId });
  return { data: response, statusCode: 200, statusText: 'OK' };
}

export async function addAgendaFavorite(agendaFavorite) {
  const response = await createAgendaFavorites({ body: agendaFavorite });
  return { data: response, statusCode: 200, statusText: 'OK' };
}

export async function removeAgendaFavorite(agendaFavorite) {
  const response = await destroyAgendaFavorites({ id: agendaFavorite.id });
  return { data: response, statusCode: 200, statusText: 'OK' };
}

export async function getAgoraToken(channelName, suffix, user_id, is_attendee) {
  const channelId = channelName.split('|')[0];

  const isUngatedTechCheck = getCurrentRoute().name === 'UngatedTechCheck';
  if (isUngatedTechCheck) {
    const res = await createUnprivilegedAgoraTokenV2({ body: { suffix } });
    return { data: { token: res.token } };
  }

  const channelSuffix = channelName.split('|')[1] ? '|' + channelName.split('|')[1] : '';

  if (is_attendee) {
    const res = await createPrivilegedAgoraToken({
      body: {
        suffix,
        channel_suffix: channelSuffix,
        room: channelId,
        user: user_id
      }
    });
    return { data: { token: res.token } };
  }

  const routes = getCurrentRoute().name.split('-');
  const currentRouteName = routes.length >= 2 ? routes[1] : routes[0];

  const isDiscussionRoom = currentRouteName === 'DiscussionRoom';
  const body = { suffix, channel_suffix: channelSuffix };

  if (isDiscussionRoom) {
    const response = await agoraTokenDiscussionGroups({ id: channelId, body });
    return { data: response, statusCode: 200, statusText: 'OK' };
  } else {
    const response = await agoraTokenBroadcasts({ id: channelId, body });
    return { data: response, statusCode: 200, statusText: 'OK' };
  }
}

export async function getAgoraBulkToken(payload) {
  const response = await bulkCreateUnprivilegedAgoraTokens({ body: payload });
  return { data: response, statusCode: 200, statusText: 'OK' };
}

export async function getSurveyIframeConfig(eventId) {
  const response = await getCventEventSurvey({ event_id: eventId });
  return { data: response, statusCode: 200, statusText: 'OK' };
}

// Transforms raw participant data into a standardized user object
export function transformParticipantToUser(participantData, eventId) {
  const {
    street_address: streetAddress = null,
    address_line_2: addressLine2 = null,
    city = null,
    state = null,
    country = null,
    zip_code: zipCode = null,
    user_id: userId = null,
    username = null,
    mmid = null,
    status = null,
    company = null,
    title = null,
    description = null,
    linkedin_url: linkedinUrl = null,
    twitter_url: twitterUrl = null,
    profile_picture_url: profilePictureUrl = null,
    fullname,
    blocked
  } = participantData ?? {};

  const location = `${streetAddress}, ${addressLine2}, ${city}, ${state}, ${country}, ${zipCode}`;

  const result = {
    event: eventId,
    id: userId,
    username,
    mmid,
    is_registered: status === 'Registered',
    is_admin: false,
    first_name: fullname,
    last_name: '',
    company,
    title,
    description,
    linkedin_url: linkedinUrl,
    twitter_url: twitterUrl,
    organization: null,
    profile_picture_url: profilePictureUrl,
    extra_data: null,
    location,
    track: null,
    event_role: 0,
    image: profilePictureUrl,
    tags: [],
    ticket_codes: [],
    thumbnail: profilePictureUrl,
    mediumimage: profilePictureUrl,
    blocked,
    entered: true,
    user: {
      id: userId,
      username,
      mmid,
      is_registered: status === 'Registered',
      is_admin: false,
      first_name: fullname,
      last_name: '',
      company,
      title,
      description,
      linkedin_url: linkedinUrl,
      twitter_url: twitterUrl,
      organization: null,
      profile_picture_url: profilePictureUrl,
      extra_data: null,
      location
    }
  };
  return result;
}
